import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useParams } from 'react-router-dom';
import { getStrawStageContentById } from '../../services/admin/admin-straw-stage-content.service';
import { useAdminStrawStageContentMutations } from '../../hooks/use-admin-straw-stage-content-mutations';
import styled from 'styled-components';
import { t } from 'i18next';
import breakpoints from '../../design-system/breakpoints';
import Variables from '../../design-system/variables';
import OvBackButton from '../UI/atoms/OvBackButton';
import OvTable from '../UI/molecules/OvTable';
import OvLoadingIndicator from '../UI/atoms/OvLoadingIndicator';
import OvErrorMessage from '../UI/atoms/OvErrorMessage';
import { StrawStageContentDataUtils } from '../../common/utils/services/straw-stage-content-data-utils';

const StrawStageContentDetails = () => {
  const { strawStageContentId }: any = useParams();

  const {
    data: strawStageContent,
    isPending: isGetStrawStageContentPending,
    isFetching: isGetStrawStageContentFetching,
    error: getStrawStageContentError,
  } = useQuery({
    queryKey: ['straw-stage-content', strawStageContentId],
    queryFn: () => getStrawStageContentById(strawStageContentId),
    retry: 3,
    enabled: !!strawStageContentId,
  });

  const { updateStrawStageContent } = useAdminStrawStageContentMutations(
    strawStageContentId!
  );

  const isLoading =
    isGetStrawStageContentFetching || isGetStrawStageContentPending;

  const allErrors = [
    getStrawStageContentError,
    updateStrawStageContent.error,
  ].filter((e): e is Error => !!e);

  return (
    <DetailPageWrapper>
      <DetailsHeader>
        <DetailsWrapper>
          <OvBackButton label={t('common.actions.back')} />
          {`${strawStageContent?.name} (${strawStageContent?.straw_stage})`}
        </DetailsWrapper>

        <span>
          {allErrors.map((err, i) => (
            <OvErrorMessage key={i} message={err.message} />
          ))}
        </span>
      </DetailsHeader>

      <DetailPageContentWrapper>
        <TableWrapper>
          <OvTable
            data={StrawStageContentDataUtils.mapStrawStageContentToTableData(
              strawStageContent
            )}
            onSaveRowValue={updateStrawStageContent.mutate}
            editable={true}
          />
        </TableWrapper>
      </DetailPageContentWrapper>

      {isLoading && <OvLoadingIndicator position="fixed" />}
    </DetailPageWrapper>
  );
};

export default StrawStageContentDetails;

const DetailPageWrapper = styled.div`
  margin-left: 1rem;
  border-radius: ${Variables.borderRadius.SMALL};
`;

const DetailsHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  font-weight: bold;
`;

const DetailsWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const DetailPageContentWrapper = styled.div`
  display: flex;
  @media (max-width: ${breakpoints.md}) {
    flex-direction: column;
  }
`;

const TableWrapper = styled.div`
  flex-grow: 1;
  align-self: flex-start;
  margin-left: 12px;
  width: 100%;

  @media (max-width: ${breakpoints.md}) {
    margin-left: 0;
    margin-top: 12px;
    margin-bottom: 12px;
  }
`;
